import React from 'react';
import Button from '../UIElements/Button';

// Session flag so a failed reload does not loop: if reloading did not fix it, we
// show the fallback UI instead of reloading again.
const RELOAD_FLAG = 'zelp_chunk_reload_attempted';

// Clear the one-shot flag and reload — used by the manual retry button.
const reloadClearingFlag = () => {
    try {
        sessionStorage.removeItem(RELOAD_FLAG);
    } catch { /* ignore */ }
    window.location.reload();
};

// A stale-chunk failure after a deploy: the lazy import points at a hashed file
// that no longer exists, so the request comes back as index.html (or 404) and the
// browser refuses to run it. Match the various shapes browsers/bundlers use.
const isChunkLoadError = (error) => {
    if (!error) return false;
    const name = error.name || '';
    const message = error.message || '';
    return (
        name === 'ChunkLoadError'
        || /Loading chunk [\w-]+ failed/i.test(message)
        || /Loading CSS chunk [\w-]+ failed/i.test(message)
        || /error loading dynamically imported module/i.test(message)
        || /failed to fetch dynamically imported module/i.test(message)
    );
};

/**
 * Top-level boundary around the lazy-loaded routes.
 *
 * Its main job is recovering from stale-chunk errors after a deploy: when a hashed
 * chunk 404s, it reloads the page ONCE to pick up the fresh index.html and chunk
 * map, which is invisible to the user beyond a blink. Any other error (or a chunk
 * error that survives the reload) renders a friendly fallback with a manual retry
 * rather than a blank white screen.
 */
class ChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidMount() {
        // A clean mount means the chunks loaded fine — reset the one-shot guard so a
        // later deploy in the same session can auto-recover too. (Skipped when this
        // mount is already the fallback, so a reload that did not help won't loop.)
        if (!this.state.hasError) {
            try {
                sessionStorage.removeItem(RELOAD_FLAG);
            } catch { /* ignore */ }
        }
    }

    componentDidCatch(error) {
        let alreadyReloaded = false;
        try {
            alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
        } catch { /* sessionStorage unavailable — fall through to the fallback UI */ }

        if (isChunkLoadError(error) && !alreadyReloaded) {
            try {
                sessionStorage.setItem(RELOAD_FLAG, '1');
            } catch { /* ignore */ }
            window.location.reload();
        }
    }

    render() {
        if (this.state.hasError) {
            // A chunk error normally reloads immediately (blank blink); this UI is for
            // the surviving cases — a non-chunk crash, or a reload that did not help.
            return (
                <div
                    className="chunk-error-boundary"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                        minHeight: '100vh',
                        padding: '2rem',
                        textAlign: 'center',
                    }}>
                    <h1>Something went wrong</h1>
                    <p>Please reload the page. If the problem continues, contact ZELP.</p>
                    <Button
                        label="Reload"
                        icon="zelpicon-refresh"
                        iconVersion={2}
                        onClick={reloadClearingFlag} />
                </div>
            );
        }
        return this.props.children;
    }
}

export default ChunkErrorBoundary;
